"use client"

import { Plus, Upload, X } from "lucide-react"
import type React from "react"
import { useState } from "react"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card } from "#/components/ui/card"
import { Input } from "#/components/ui/input"
import { Label } from "#/components/ui/label"
import { Textarea } from "#/components/ui/textarea"
import { authClient } from "#/lib/auth/client"

interface ArticlePostFormProps {
  onPostCreated: () => void
}

export function ArticlePostForm({ onPostCreated }: ArticlePostFormProps) {
  const [title, setTitle] = useState("")
  const [content, setContent] = useState("")
  const [coverImage, setCoverImage] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>([])
  const [newTag, setNewTag] = useState("")
  const [isPublishing, setIsPublishing] = useState(false)
  const { session } = authClient.useSession()

  const handleCoverImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => {
        setCoverImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim()) && tags.length < 5) {
      setTags([...tags, newTag.trim()])
      setNewTag("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !content.trim() || !session) return

    setIsPublishing(true)

    // Mock publishing delay
    await new Promise(resolve => setTimeout(resolve, 2000))

    // In a real app, you would create the article post
    console.log("Creating article post:", { title, content, coverImage, tags })

    setIsPublishing(false)
    onPostCreated()
  }

  const estimatedReadTime = Math.max(
    1,
    Math.ceil(content.split(" ").length / 200),
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Article Title</Label>
        <Input
          id="title"
          placeholder="Enter your article title..."
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={100}
        />
      </div>

      <div className="space-y-4">
        <Label>Cover Image (Optional)</Label>
        {!coverImage ? (
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Add a cover image
            </p>
            <Input
              type="file"
              accept="image/*"
              onChange={handleCoverImageUpload}
              className="max-w-xs mx-auto"
            />
          </div>
        ) : (
          <Card className="relative">
            <img
              src={coverImage || "/placeholder.svg"}
              alt="Cover preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => setCoverImage(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </Card>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Article Content</Label>
        <Textarea
          id="content"
          placeholder="Write your article content here... You can include multiple paragraphs and detailed thoughts."
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={12}
          className="min-h-[300px]"
        />
        <div className="text-right text-sm text-muted-foreground">
          Estimated read time: {estimatedReadTime} min
        </div>
      </div>

      <div className="space-y-4">
        <Label>Tags (Optional)</Label>
        <div className="flex gap-2 flex-wrap">
          {tags.map(tag => (
            <Badge
              key={tag}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        {tags.length < 5 && (
          <div className="flex gap-2">
            <Input
              placeholder="Add a tag..."
              value={newTag}
              onChange={e => setNewTag(e.target.value)}
              onKeyPress={e =>
                e.key === "Enter" && (e.preventDefault(), addTag())
              }
              maxLength={20}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addTag}
              disabled={!newTag.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!title.trim() || !content.trim() || isPublishing}
      >
        {isPublishing ? "Publishing..." : "Publish Article"}
      </Button>
    </form>
  )
}
